import type { MakerAgent } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleCheck, Clock, Info, TriangleAlert } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Note } from '@/components/marketplace/maker/BuilderShell.tsx'
import { publishedStateHint } from '@/components/marketplace/maker/publish-wording.ts'
import {
  readDismissedOutcomes,
  rememberDismissedOutcome,
  type SharingBanner,
  sharingBanner
} from '@/components/marketplace/maker/sharing-request.ts'
import { Badge, Button } from '@/components/ui/primitives.tsx'
import { useListingTransition } from '@/hooks/use-maker.ts'
import { useWithdrawSharingRequest } from '@/hooks/use-sharing.ts'
import { describeApiError, type Messages, useI18n } from '@/i18n/index.ts'

type Pending = 'unpublish' | 'archive'

/**
 * The two confirmations, built from the catalogue rather than captured from it.
 *
 * This was a module-level `Record` reading `strings.unpublishConfirm` at import. It is the
 * F-3 shape exactly: the sentences are read once, when the bundle evaluates, and the dialog
 * then asks its question in whichever language was live at that moment for the rest of the
 * session — with nothing failing. A function of the catalogue, called from a component body,
 * is the same table without the capture.
 */
const confirmationsFor = (
  t: Messages['marketplace']['publish']['lifecycle']
): Record<Pending, { title: string; body: string; extra?: string }> => ({
  unpublish: { title: t.unpublishConfirm, body: t.unpublishBody },
  archive: { title: t.archiveConfirm, body: t.archiveBody, extra: t.archiveNameHold }
})

/**
 * State, what its maintainers can do about it, and where its sharing request stands.
 *
 * Every transition here is one the state machine actually has (AGM-040) — there is no "delete",
 * because a published agent's conversations outlive its listing and stay readable by design.
 * Each confirmation says what happens to the people already using it, and — when a request is
 * waiting for review — that taking the agent down closes it (D-51).
 *
 * Bringing a retired agent back chooses who can use it, exactly as publishing does (D-50), so it
 * opens the publish dialog rather than a yes/no here.
 *
 * The sharing request (GRP-081): while it waits, the bar says for whom and since when, with
 * *Withdraw request* for any current maintainer (D-52); once decided or closed, the outcome stays
 * until whoever reads it dismisses it — in this browser, per reader.
 */
export const LifecycleBar = ({
  agent,
  action,
  canPublish,
  isStakeholder,
  viewerId,
  onPublish,
  onRestore
}: {
  agent: MakerAgent
  /** The primary button's words, which follow what publishing would do (`publishWording`). */
  action: string
  /** Whether the transitions are this reader's to make: its maintainers, or an administrator. */
  canPublish: boolean
  /**
   * Whether the reader maintains it by a stake — its owner, or a manager of its group — which is a
   * different question from `canPublish`, asked for a different reason: one draws buttons, the
   * other decides whether "only the people who maintain it can…" is worth printing (PRD-22 F-8).
   */
  isStakeholder: boolean
  viewerId: string | null
  onPublish: () => void
  onRestore: () => void
}) => {
  // "Legal and Ops" is `Intl.ListFormat`'s job, so the banner's subject is built here where the
  // active locale is readable rather than inside the pure module that words it. The words come
  // the same way, for the same reason (PRD-27 F-3).
  const { formatList, t } = useI18n()
  const strings = t.marketplace.publish.lifecycle
  const publishStrings = t.marketplace.publish
  const barRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLButtonElement | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissedOutcomes())
  const transition = useListingTransition(agent.agentId)
  const withdraw = useWithdrawSharingRequest()

  const confirmation = pending ? confirmationsFor(strings)[pending] : null
  const banner = sharingBanner(publishStrings, { formatList }, agent.sharingRequest, dismissed)
  const waiting = banner?.kind === 'waiting'
  // Published reads off the LIVE audience — what people can reach right now — while the button
  // reads off the choice being made. They are different questions and, mid-edit, different answers.
  const hint =
    agent.state === 'published'
      ? publishedStateHint(publishStrings, agent.audience, agent.owner, viewerId)
      : strings.stateHint[agent.state]

  // A control that disappears under the reader (the banner they dismissed, the request they
  // withdrew) must not drop focus on the body: it goes to the bar that held it.
  const refocus = () => requestAnimationFrame(() => barRef.current?.focus())

  // The confirmation takes focus when it opens, so a keyboard reader lands on the question rather
  // than past it; cancelling hands focus back to the button that asked it.
  useEffect(() => {
    if (pending) confirmRef.current?.focus()
  }, [pending])

  const ask = (next: Pending, opener: HTMLButtonElement) => {
    openerRef.current = opener
    setPending(next)
  }
  const cancel = () => {
    setPending(null)
    requestAnimationFrame(() => openerRef.current?.focus())
  }

  const dismiss = (id: string) => {
    setDismissed(rememberDismissedOutcome(id))
    refocus()
  }

  return (
    <div ref={barRef} tabIndex={-1} className="card rounded-panel px-3.5 py-3 focus:outline-none">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <Badge tone={agent.state === 'published' ? 'grounded' : agent.state === 'suspended' ? 'alert' : 'neutral'}>
          {strings.state[agent.state]}
        </Badge>
        <span className="text-[12.5px] text-fg-muted">{hint}</span>

        <div className="ml-auto flex flex-wrap gap-1.5">
          {canPublish && agent.state !== 'suspended' && agent.state !== 'archived' && (
            <Button size="sm" variant="primary" onClick={onPublish}>
              {action}
            </Button>
          )}
          {canPublish && agent.state === 'published' && (
            <Button size="sm" onClick={(event) => ask('unpublish', event.currentTarget)}>
              {strings.unpublish}
            </Button>
          )}
          {canPublish && (agent.state === 'published' || agent.state === 'draft') && (
            <Button size="sm" variant="ghost" onClick={(event) => ask('archive', event.currentTarget)}>
              {strings.archive}
            </Button>
          )}
          {canPublish && agent.state === 'archived' && (
            <Button size="sm" onClick={onRestore}>
              {strings.restore}
            </Button>
          )}
        </div>
      </div>

      {banner && (
        <RequestBanner
          banner={banner}
          canWithdraw={canPublish}
          withdrawing={withdraw.isPending}
          withdrawFailed={withdraw.isError}
          onWithdraw={(id) => withdraw.mutate(id, { onSuccess: refocus })}
          onDismiss={dismiss}
        />
      )}

      {agent.state === 'archived' && (
        <p className="mt-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.archiveNameHold}</p>
      )}

      {/* Printed only where it is true: a co-editor, or an invited one. An administrator reads
          neither this nor a claim to ownership — they simply get the controls. */}
      {!canPublish && !isStakeholder && (
        <p className="mt-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-subtle">
          {publishStrings.blockedNotOwner}
        </p>
      )}

      {confirmation && pending && (
        <div
          ref={confirmRef}
          tabIndex={-1}
          className="mt-3 rounded-tile border border-rule-strong bg-card-solid px-3.5 py-3 shadow-glass focus:outline-none"
        >
          <p className="text-[13.5px] font-semibold tracking-[-0.014em]">{confirmation.title}</p>
          <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{confirmation.body}</p>
          {confirmation.extra && (
            <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{confirmation.extra}</p>
          )}
          {waiting && (
            <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.closesRequest}</p>
          )}
          <div className="mt-2.5 flex gap-2">
            <Button
              size="sm"
              variant={pending === 'archive' ? 'danger' : 'primary'}
              disabled={transition.isPending}
              onClick={() =>
                transition.mutate(pending, {
                  onSuccess: () => {
                    setPending(null)
                    // The button that asked is gone with the state it belonged to.
                    refocus()
                  }
                })
              }
            >
              {strings.confirm}
            </Button>
            <Button size="sm" variant="ghost" onClick={cancel}>
              {strings.cancel}
            </Button>
          </div>
          {transition.isError && (
            <div className="mt-2.5">
              <Note tone="warn" icon={<TriangleAlert size={13} strokeWidth={2.1} />}>
                {strings.failed} {describeApiError(t, transition.error)}
              </Note>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const TONE: Record<SharingBanner['kind'], { box: string; icon: ReactNode }> = {
  waiting: { box: 'border-rule bg-card-solid', icon: <Clock size={13} strokeWidth={2.1} className="text-fg-muted" /> },
  approved: {
    box: 'border-grounded/35 bg-grounded-soft',
    icon: <CircleCheck size={13} strokeWidth={2.1} className="text-grounded" />
  },
  declined: {
    box: 'border-alert/35 bg-alert-soft',
    icon: <TriangleAlert size={13} strokeWidth={2.1} className="text-alert" />
  },
  closed: { box: 'border-rule bg-card-solid', icon: <Info size={13} strokeWidth={2.1} className="text-fg-muted" /> }
}

const RequestBanner = ({
  banner,
  canWithdraw,
  withdrawing,
  withdrawFailed,
  onWithdraw,
  onDismiss
}: {
  banner: SharingBanner
  canWithdraw: boolean
  withdrawing: boolean
  withdrawFailed: boolean
  onWithdraw: (id: string) => void
  onDismiss: (id: string) => void
}) => {
  const { formatAgo, t } = useI18n()
  const r = t.marketplace.publish.lifecycle.request
  const lines: string[] = (() => {
    switch (banner.kind) {
      case 'waiting':
        return [r.waiting(banner.whom, formatAgo(banner.at)), ...(banner.by ? [r.waitingBy(banner.by)] : [])]
      case 'approved':
        return [
          r.approved(banner.whom),
          r.approvedBy(banner.by, formatAgo(banner.at)),
          ...(banner.note ? [r.note(banner.note)] : [])
        ]
      case 'declined':
        return [
          r.declined(banner.whom),
          r.declinedBy(banner.by, formatAgo(banner.at)),
          ...(banner.note ? [r.note(banner.note)] : [])
        ]
      case 'closed':
        return [r.closed(banner.whom), ...(banner.reason ? [r.closedReason[banner.reason]] : [])]
    }
  })()

  return (
    <div
      role="status"
      className={clsx(
        'mt-3 flex flex-wrap items-start gap-x-3 gap-y-2 rounded-tile border px-3 py-2.5',
        TONE[banner.kind].box
      )}
    >
      <span className="mt-[2px] shrink-0" aria-hidden>
        {TONE[banner.kind].icon}
      </span>
      <div className="min-w-0 flex-1 basis-[14rem] text-pretty text-[12.5px] leading-[1.5]">
        <p className="font-semibold tracking-[-0.012em]">{lines[0]}</p>
        {lines.slice(1).map((line) => (
          <p key={line} className="mt-0.5 text-fg-muted">
            {line}
          </p>
        ))}
        {banner.kind === 'waiting' && withdrawFailed && <p className="mt-1 text-alert">{r.withdrawFailed}</p>}
      </div>
      {banner.kind === 'waiting' ? (
        canWithdraw && (
          <Button size="sm" disabled={withdrawing} onClick={() => onWithdraw(banner.id)}>
            {withdrawing ? r.withdrawing : r.withdraw}
          </Button>
        )
      ) : (
        <Button size="sm" variant="ghost" onClick={() => onDismiss(banner.id)}>
          {r.dismiss}
        </Button>
      )}
    </div>
  )
}
